import { ChatHTTP } from './ChatHTTP';
import { ChatWS } from './ChatWS';
import {
	WithClientID,
	IChatMessages,
	IChatCommandMessage,
	IRawMessageEcho,
	CloseEventType,
} from '../types';
import { IChatProvider } from '../types/provider/IChat';
import { ChatMessageEvent } from '../models/ChatEvents';
import {
	ChatEchoEvent,
	ChatErrorEvent,
	checkRawMessageEcho,
	checkRawMessage,
	ROtoCM,
	CMOtoRI,
	IResources,
} from '../models';
import { BaseChatProvider } from './BaseChatProvider';
import { isFileResourceMessage } from '../utils/checkMessage/checkChatMessageType';
import { getNormalizedIsoDate } from '../utils/getNormalizedIsoDate';
import { IConnectionConfiguration } from 'src/api/configuration/types';
import {
	asyncRawMediaMessageAdapter_IN,
	syncRawMessageAdapter_IN,
} from './adapter';

export class ChatProvider extends BaseChatProvider implements IChatProvider {
	protected wsConnection!: ChatWS;
	protected httpConnection!: ChatHTTP;

	constructor(
		protected baseUrl: string,
		protected configuration: Partial<IConnectionConfiguration>,
		protected withCredentials?: boolean,
	) {
		super();
	}

	init(configuration?: Partial<IConnectionConfiguration>) {
		// A freshly fetched configuration replaces the current one: transports
		// must pick up updated values (e.g. a renewed JWT after a restart).
		if (configuration) {
			this.configuration = configuration;
		}
		console.log('Initializing ChatProvider...');
		this.wsConnection?.clear();
		this.httpConnection = new ChatHTTP(
			this.baseUrl,
			this.configuration,
			this.withCredentials,
		);
		console.log('ChatHTTP instance created');
		const wsConnection = new ChatWS(this.baseUrl, this.configuration);
		console.log('ChatWS instance created');

		// bind listener
		wsConnection.onOpen(this.notify);
		wsConnection.onClose(this.notify);
		wsConnection.onError(this.notify);
		wsConnection.onMessageEvent((ev) => {
			this.notify(ev);

			if (typeof ev.value === 'object') {
				const message = ev.value;
				if (checkRawMessage(message)) {
					try {
						const simpleMessage = syncRawMessageAdapter_IN(message);
						this.notify(new ChatMessageEvent(simpleMessage));
					} catch {
						asyncRawMediaMessageAdapter_IN(message)
							.then((chatMessage) => {
								this.notify(new ChatMessageEvent(chatMessage));
							})
							.catch((error) => {
								this.notify(new ChatErrorEvent(ev.value));
							});
					}
					// ROtoCM(ev.value).then((chatMessage) => {
					// 	this.notify(new ChatMessageEvent(chatMessage));
					// });
				} else if (checkRawMessageEcho(ev.value)) {
					const echo: IRawMessageEcho = {
						...ev.value,
						timestamp: getNormalizedIsoDate(ev.value.timestamp).iso,
					};
					this.notify(new ChatEchoEvent(echo));
				}

				if (!!ev.value.errors) {
					this.notify(new ChatErrorEvent(ev.value));
				}
			}
		});
		this.wsConnection = wsConnection;
	}

	initConnection(tstamp?: string) {
		return this.wsConnection.init(tstamp);
	}

	close(code?: number, reason?: CloseEventType) {
		// calls connection close (so it can notify its close event)
		return this.httpConnection.close().then(() => {
			this.wsConnection.close(code, reason);
		});
	}

	sendMessage<
		T extends WithClientID<Exclude<IChatMessages, IChatCommandMessage>>,
	>(message: T): Promise<void> {
		if (isFileResourceMessage(message)) {
			return this.httpConnection
				.sendFileMessage(message._data)
				.then((resource) => {
					const msg = {
						...message,
						_data: resource,
					};
					return this.sendMessage(msg);
				});
		} else {
			const rawIn = CMOtoRI(message as any);
			return this.wsConnection.sendMessage(rawIn);
		}
	}

	getHistory(timestamp?: string, limit?: number) {
		return this.httpConnection
			.getHistory(timestamp, limit)
			.then((raws) =>
				Promise.all(
					raws
						.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
						.map(ROtoCM),
				),
			);
	}

	getResourceUrl<T extends IResources>(resource: T) {
		if (resource.resource_id && !resource.data) {
			// const baseUrl = new URL(
			// 	this.configuration.http_url?.endsWith('/')
			// 		? this.configuration.http_url
			// 		: this.configuration.http_url + '/',
			// 	this.baseUrl,
			// );
			const url = new URL(
				`./files/${resource.resource_id}`,
				this.httpConnection.url,
			).href;
			return url;
		} else if (resource.data) {
			return resource.data;
		}
		throw new Error('Wrong resource');
	}
}
